import React, { useEffect, useLayoutEffect, useCallback, useState, useRef, useMemo } from 'react';
import { View, StyleSheet, Platform, ScrollView, RefreshControl } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import useRequireAuth from '@/hooks/useRequireAuth';
import { useExploreStore, useExploreLightboxStore, useModalStore } from '@/store';
import { trackEvent, trackPageView } from '@/constants/utils/analytics';
import { colorForSlug } from '@/utils/tasks_catalog';
import ExploreFeed from '@/components/Explore/ExploreFeed';
import ExploreFloatingInput from '@/components/Explore/ExploreFloatingInput';
import ExploreMediaTypeTabs from '@/components/Explore/ExploreMediaTypeTabs';
import ExploreMobileToolbar from '@/components/Explore/ExploreMobileToolbar';
import ExplorePageListHeader, { ExplorePageChromeContext } from '@/components/Explore/ExplorePageListHeader';
import ExploreSortDropdown from '@/components/Dropdowns/ExploreSortDropdown';
import ExploreCategoryDropdown from '@/components/Dropdowns/ExploreCategoryDropdown';
import CatalogBrowseShell, { CATALOG_VIEW_MODES } from '@/components/Catalog/CatalogBrowseShell';
import CatalogDiscoveryHero from '@/components/Catalog/CatalogDiscoveryHero';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import Text from '@/components/Typography/Text';
import MetaHead from '@/components/MetaHead';
import ToolPageCopy from '@/components/Tools/ToolPageCopy';
import { normalizeCategoryPageCopy, buildCategoryFaqBlock } from '@/utils/pageCopyBlocks';

// Static SEO payload — same content always renders into the prerendered HTML
// so crawlers see proper title/description/canonical/Schema even before the
// JS-driven feed populates.
const EXPLORE_SEO_STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'CollectionPage',
      name: 'Explore — Curated AI Images, Videos, Music & 3D | Gab AI',
      description:
        'Browse Gab AI’s curated Explore gallery of AI-generated images, videos, music, and 3D—editorially selected examples you can open to remix prompts, save favorites, and start new generations.',
      url: 'https://gab.ai/explore',
      isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: 'https://gab.ai' },
      inLanguage: 'en-US',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://gab.ai' },
        { '@type': 'ListItem', position: 2, name: 'Explore', item: 'https://gab.ai/explore' },
      ],
    },
  ],
};

const isWeb = Platform.OS === 'web';
const isNative = Platform.OS === 'ios' || Platform.OS === 'android';

const VALID_MEDIA_TYPES = ['image', 'video', 'music', '3d'];
const VALID_SORTS = ['featured', 'newest', 'popular'];

const VIEW_MODES = CATALOG_VIEW_MODES;

/** Persisted right before in-app category navigations so the catalog scroll position is restored (web). */
const EXPLORE_CATALOG_SCROLL_KEY = 'gab_explore_catalog_scroll_y';

function persistExploreCatalogScroll(scrollYRef) {
  if (!isWeb || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      EXPLORE_CATALOG_SCROLL_KEY,
      String(Math.max(0, Math.floor(scrollYRef.current))),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function buildExploreQueryString(filters) {
  const params = new URLSearchParams();
  if (filters.mediaType && VALID_MEDIA_TYPES.includes(filters.mediaType)) params.set('type', filters.mediaType);
  if (filters.sort && filters.sort !== 'featured') params.set('sort', filters.sort);
  if (filters.category) params.set('category', filters.category);
  if (filters.model) params.set('model', filters.model);
  if (filters.search) params.set('q', filters.search);
  if (filters.favorites) params.set('favorites', 'true');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function normalizeExploreCategorySlug(propSlug, queryCategory) {
  const raw = propSlug ?? queryCategory;
  if (raw == null || raw === '') return undefined;
  const s = Array.isArray(raw) ? raw[0] : raw;
  if (typeof s !== 'string') return undefined;
  const trimmed = s.trim();
  return trimmed || undefined;
}

export const ExplorePageScreen = React.memo(({ categorySlug: categorySlugProp = null } = {}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM, isMD, isXL } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const queryParams = useLocalSearchParams();
  // Path-based routes like /explore/category/<slug> pass the slug as a prop so
  // the same feed component is reused. The prop wins over the legacy
  // ?category=<slug> query string when both are present.
  const activeExploreCategorySlug = useMemo(
    () => normalizeExploreCategorySlug(categorySlugProp, queryParams.category),
    [categorySlugProp, queryParams.category],
  );
  const items = useExploreStore(state => state.items);
  const featuredItems = useExploreStore(state => state.featuredItems);
  const categories = useExploreStore(state => state.categories);
  const filterMediaType = useExploreStore(state => state.filters.mediaType);
  const filterSort = useExploreStore(state => state.filters.sort);
  const filterCategory = useExploreStore(state => state.filters.category);
  const filterFavorites = useExploreStore(state => state.filters.favorites);
  const isLoading = useExploreStore(state => state.isLoading);
  const paginationHasMore = useExploreStore(state => state.pagination.hasMore);
  const paginationPage = useExploreStore(state => state.pagination.page);
  const paginationTotal = useExploreStore(state => state.pagination.total);
  const feedError = useExploreStore(state => state.errors.feed);
  const fetchFeed = useExploreStore(state => state.actions.fetchFeed);
  const fetchCategories = useExploreStore(state => state.actions.fetchCategories);
  const fetchItem = useExploreStore(state => state.actions.fetchItem);
  const applyFiltersAndRefetch = useExploreStore(state => state.actions.applyFiltersAndRefetch);

  const openExploreLightbox = useExploreLightboxStore(state => state.actions.openExploreLightbox);
  const exploreLightboxIsOpen = useExploreLightboxStore(state => state.isOpen);

  const openModal = useModalStore(state => state.actions.openModal);
  const requireAuth = useRequireAuth();

  const [prefill, setPrefill] = useState({ prompt: '', mediaType: null, modelId: null });
  const [searchText, setSearchText] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [viewMode, setViewMode] = useState(VIEW_MODES.GRID);
  const [catalogBodyWidth, setCatalogBodyWidth] = useState(null);
  const [pageScrollRefreshing, setPageScrollRefreshing] = useState(false);
  const focusedIndexRef = useRef(-1);
  const searchDebounceRef = useRef(null);
  const scrollViewRef = useRef(null);
  const scrollYRef = useRef(0);

  const legacyExploreCategoryParam = Array.isArray(queryParams.category)
    ? queryParams.category[0]
    : queryParams.category;
  const catalogScrollRestoreKey = `${categorySlugProp || ''}|${legacyExploreCategoryParam || ''}`;

  useLayoutEffect(() => {
    if (!isWeb || typeof sessionStorage === 'undefined') return;
    let raw;
    try {
      raw = sessionStorage.getItem(EXPLORE_CATALOG_SCROLL_KEY);
    } catch {
      return;
    }
    if (raw == null) return;
    try {
      sessionStorage.removeItem(EXPLORE_CATALOG_SCROLL_KEY);
    } catch {
      /* ignore */
    }
    const y = parseInt(raw, 10);
    if (!Number.isFinite(y) || y < 0) return;
    requestAnimationFrame(() => {
      scrollViewRef.current?.scrollTo?.({ y, animated: false });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed restore only
  }, [catalogScrollRestoreKey]);

  // Sync URL / route props → store and refetch the feed. A single effect avoids
  // double-fetching with a separate "init" block (both ran on mount before).
  useEffect(() => {
    const nextFilters = {};
    if (queryParams.type && VALID_MEDIA_TYPES.includes(queryParams.type)) nextFilters.mediaType = queryParams.type;
    if (queryParams.sort && VALID_SORTS.includes(queryParams.sort)) nextFilters.sort = queryParams.sort;
    nextFilters.category = activeExploreCategorySlug || null;
    if (queryParams.model) nextFilters.model = queryParams.model;
    if (queryParams.q) {
      nextFilters.search = queryParams.q;
      setSearchText(queryParams.q);
    }
    nextFilters.favorites = queryParams.favorites === 'true';
    applyFiltersAndRefetch(nextFilters);

    const { filters: mergedFilters, categoriesFetchedMediaKey } = useExploreStore.getState();
    const categoriesMediaKey = mergedFilters.mediaType ?? 'all';
    if (categoriesFetchedMediaKey !== categoriesMediaKey) {
      fetchCategories();
    }
  }, [activeExploreCategorySlug, queryParams.model, queryParams.type, queryParams.sort, queryParams.favorites, queryParams.q, applyFiltersAndRefetch, fetchCategories]);

  useEffect(() => {
    trackPageView('Explore');

    // Open the lightbox if a deep link supplied an item slug. Two URL shapes
    // are supported:
    //   - Path-based (canonical):  /explore/item/<slug>          → params.slug
    //   - Query string:            /explore?item=<slug>           → params.item
    const deepLinkItemSlug = queryParams.slug || queryParams.item;
    if (deepLinkItemSlug) {
      fetchItem(deepLinkItemSlug).then(fetched => {
        if (fetched) openExploreLightbox(fetched, useExploreStore.getState().items);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time mount; URL sync is handled above
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const handleLoadMore = useCallback(() => {
    if (paginationHasMore && !isLoading) {
      fetchFeed(paginationPage + 1);
    }
  }, [paginationHasMore, paginationPage, isLoading, fetchFeed]);

  const scrollLoadMoreLatch = useRef(false);
  const handleExplorePageScroll = useCallback((e) => {
    scrollYRef.current = e.nativeEvent.contentOffset?.y ?? 0;
    const { contentOffset, layoutMeasurement, contentSize } = e.nativeEvent;
    if (!layoutMeasurement?.height || !contentSize?.height) return;
    const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);
    if (distanceFromBottom > 900) return;
    if (scrollLoadMoreLatch.current) return;
    scrollLoadMoreLatch.current = true;
    handleLoadMore();
    setTimeout(() => {
      scrollLoadMoreLatch.current = false;
    }, 650);
  }, [handleLoadMore]);

  const syncFiltersToUrl = useCallback((filterOverrides = {}) => {
    if (!isWeb) return;
    const current = useExploreStore.getState().filters;
    const merged = { ...current, ...filterOverrides };
    // When we're on the path-based /explore/category/<slug> route, keep the
    // category in the path (cleaner URL, better SEO) and strip it from the
    // query string we serialize.
    let basePath = '/explore';
    let mergedForQs = merged;
    if (categorySlugProp) {
      basePath = `/explore/category/${encodeURIComponent(categorySlugProp)}`;
      const { category: _omit, ...rest } = merged;
      mergedForQs = rest;
    }
    const qs = buildExploreQueryString(mergedForQs);
    const path = `${basePath}${qs}`;
    try {
      window.history.replaceState(null, '', path);
    } catch (_) {}
  }, [categorySlugProp]);

  const handleDebouncedSearchChange = useCallback((text) => {
    setSearchText(text);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const query = text.trim();
      trackEvent('explore', 'explore_search', query);
      applyFiltersAndRefetch({ search: query });
      syncFiltersToUrl({ search: query });
    }, 400);
  }, [applyFiltersAndRefetch, syncFiltersToUrl]);

  const handleItemPress = useCallback((item) => {
    trackEvent('explore', 'explore_item_open', item.slug || item.id);
    openExploreLightbox(item, useExploreStore.getState().items);
  }, [openExploreLightbox]);

  const handleClearPrefill = useCallback(() => {
    setPrefill({ prompt: '', mediaType: null, modelId: null });
  }, []);

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      applyFiltersAndRefetch({}, { forceFeatured: true }),
      fetchCategories(),
    ]);
  }, [applyFiltersAndRefetch, fetchCategories]);

  const handlePageScrollRefresh = useCallback(async () => {
    setPageScrollRefreshing(true);
    try {
      await handleRefresh();
    } finally {
      setPageScrollRefreshing(false);
    }
  }, [handleRefresh]);

  const handleExploreSortSelect = useCallback((sortId) => {
    if (sortId === 'favorites') {
      const ran = requireAuth(
        () => {
          if (filterFavorites) return;
          trackEvent('explore', 'explore_filter_favorites', 'on');
          applyFiltersAndRefetch({ favorites: true });
          syncFiltersToUrl({ favorites: true });
        },
        {
          source: 'explore_favorites_filter',
          title: 'Sign in to see your Explore favorites',
          subtitle: 'Save the prompts and creations you love so they follow you across devices.',
          icon: 'heart',
        }
      );
      if (ran === undefined) {
        trackEvent('explore', 'explore_auth_required', 'favorites_filter');
      }
      return;
    }
    trackEvent('explore', 'explore_filter_sort', sortId);
    const updates = { sort: sortId };
    if (filterFavorites) updates.favorites = false;
    applyFiltersAndRefetch(updates);
    syncFiltersToUrl(updates);
  }, [requireAuth, filterFavorites, applyFiltersAndRefetch, syncFiltersToUrl]);

  const handleMediaScopePress = useCallback((mediaType) => {
    trackEvent('explore', 'explore_filter_media_type', mediaType);
    applyFiltersAndRefetch({ mediaType: mediaType ?? null, category: null });
    fetchCategories();
    syncFiltersToUrl({ mediaType: mediaType ?? null, category: null });
  }, [applyFiltersAndRefetch, fetchCategories, syncFiltersToUrl]);

  const clearExploreCategoryFacets = useCallback(() => {
    persistExploreCatalogScroll(scrollYRef);
    applyFiltersAndRefetch({ category: null });
    router.replace('/explore');
  }, [applyFiltersAndRefetch, router]);

  const handleExploreCategorySelect = useCallback((slug) => {
    trackEvent('explore', 'explore_filter_category', slug);
    persistExploreCatalogScroll(scrollYRef);
    if (!slug) {
      clearExploreCategoryFacets();
      return;
    }
    if (filterCategory === slug || categorySlugProp === slug) return;
    router.push(`/explore/category/${encodeURIComponent(slug)}`);
  }, [router, filterCategory, categorySlugProp, clearExploreCategoryFacets]);

  const handleBrowseExploreFromFavorites = useCallback(() => {
    if (!filterFavorites) return;
    handleExploreSortSelect('featured');
  }, [filterFavorites, handleExploreSortSelect]);

  const handleCatalogHeroItemPress = useCallback((row) => {
    const item = row?._item;
    if (!item) return;
    trackEvent('explore', 'explore_item_open', item.slug || item.id);
    openExploreLightbox(item, useExploreStore.getState().items);
  }, [openExploreLightbox]);

  const onCatalogBodyLayout = useCallback((e) => {
    const w = e?.nativeEvent?.layout?.width;
    if (typeof w === 'number' && w > 0) setCatalogBodyWidth(w);
  }, []);

  const catalogHeroExplore = useMemo(() => {
    const pool = [...(featuredItems || []), ...(items || [])];
    const seen = new Set();
    const out = [];
    for (const it of pool) {
      if (!it?.id || it.flags?.isTemplate) continue;
      if (seen.has(it.id)) continue;
      seen.add(it.id);
      const icon = it.mediaType === 'video' ? 'video'
        : it.mediaType === 'music' ? 'music'
          : it.mediaType === '3d' ? 'cube' : 'image';
      const name = (it.title || it.prompt || 'Explore').trim().slice(0, 48) || 'Item';
      out.push({ id: it.id, name, icon, _item: it });
      if (out.length >= 6) break;
    }
    return out;
  }, [featuredItems, items]);

  const activeCategoryFilterSlug = filterCategory || categorySlugProp || null;

  const exploreCategoriesFiltered = useMemo(() => {
    if (!categories?.length) return [];
    return categories.filter((cat) => {
      if (!filterMediaType) return true;
      if (!cat.mediaTypes?.length) return true;
      return cat.mediaTypes.includes(filterMediaType);
    });
  }, [categories, filterMediaType]);

  const exploreBrowseToolbar = useMemo(() => ({
    viewMode,
    listMode: VIEW_MODES.LIST,
    gridMode: VIEW_MODES.GRID,
    onViewModeChange: setViewMode,
  }), [viewMode]);

  const exploreDesktopToolbar = useMemo(() => ({
    ...exploreBrowseToolbar,
    leading: (
      <ExploreMediaTypeTabs
        activeType={filterMediaType}
        onChangeType={handleMediaScopePress}
      />
    ),
    trailing: (
      <>
        {paginationTotal > 0 ? (
          <Text size="xs" color="secondary" style={{ fontVariant: ['tabular-nums'] }}>
            {t('explore.categoryItems').replace('%COUNT%', String(paginationTotal))}
          </Text>
        ) : null}
        <ExploreCategoryDropdown
          categories={exploreCategoriesFiltered}
          activeCategorySlug={activeCategoryFilterSlug}
          onChangeCategory={handleExploreCategorySelect}
        />
        <ExploreSortDropdown
          activeSort={filterSort}
          filterFavorites={filterFavorites}
          onChangeSort={handleExploreSortSelect}
        />
      </>
    ),
  }), [
    exploreBrowseToolbar,
    paginationTotal,
    filterSort,
    t,
    filterFavorites,
    handleExploreSortSelect,
    filterMediaType,
    handleMediaScopePress,
    exploreCategoriesFiltered,
    activeCategoryFilterSlug,
    handleExploreCategorySelect,
  ]);

  const exploreMobileToolbar = useMemo(() => (
    <ExploreMobileToolbar
      filterMediaType={filterMediaType}
      onMediaTypeChange={handleMediaScopePress}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      categories={exploreCategoriesFiltered}
      activeCategorySlug={activeCategoryFilterSlug}
      onCategoryChange={handleExploreCategorySelect}
      activeSort={filterSort}
      filterFavorites={filterFavorites}
      onSortChange={handleExploreSortSelect}
    />
  ), [
    filterMediaType,
    handleMediaScopePress,
    viewMode,
    exploreCategoriesFiltered,
    activeCategoryFilterSlug,
    handleExploreCategorySelect,
    filterSort,
    filterFavorites,
    handleExploreSortSelect,
  ]);

  const feedListHeader = useMemo(() => (
    <ExplorePageListHeader hideSearch hideCategoryPills hideSortDropdown />
  ), []);

  useEffect(() => {
    if (!isWeb) return;
    const handleKeyDown = (e) => {
      if (exploreLightboxIsOpen) return;
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;

      const currentItems = useExploreStore.getState().items;
      if (!currentItems.length) return;

      if (e.key === 'Escape') {
        setFocusedIndex(-1);
        focusedIndexRef.current = -1;
        return;
      }

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = Math.min(focusedIndexRef.current + 1, currentItems.length - 1);
        setFocusedIndex(next);
        focusedIndexRef.current = next;
        return;
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = Math.max(focusedIndexRef.current - 1, 0);
        setFocusedIndex(prev);
        focusedIndexRef.current = prev;
        return;
      }

      if (e.key === 'Enter' && focusedIndexRef.current >= 0) {
        e.preventDefault();
        const item = currentItems[focusedIndexRef.current];
        if (item) {
          trackEvent('explore', 'explore_item_open', item.slug || item.id);
          openExploreLightbox(item, currentItems);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [exploreLightboxIsOpen, openExploreLightbox]);

  const chromeContextValue = useMemo(() => ({
    searchText,
    onSearchTextChange: handleDebouncedSearchChange,
    syncFiltersToUrl,
  }), [searchText, handleDebouncedSearchChange, syncFiltersToUrl]);

  const pageStyles = useMemo(() => StyleSheet.create({
    pageScroll: {
      flex: 1,
      width: '100%',
    },
    pageScrollContent: {
      width: '100%',
      maxWidth: isXL ? 1460 : 1240,
      alignSelf: 'center',
      flexGrow: 1,
      paddingBottom: insets.bottom + 100,
    },
    feedWidthProbe: {
      width: '100%',
    },
    footerWrap: {
      paddingHorizontal: isXS ? 16 : 28,
    },
  }), [isXS, isXL, insets.bottom]);

  const exploreHeroCategoryRecord = useMemo(() => {
    if (!activeExploreCategorySlug || !categories?.length) return null;
    return categories.find((c) => c.slug === activeExploreCategorySlug) || null;
  }, [activeExploreCategorySlug, categories]);

  const defaultDiscoveryHeadline = t('explore.discoveryHeadline') || 'Browse AI images, video, music & 3D';
  const defaultDiscoverySubtitle = t('explore.discoverySubtitle')
    || 'Open any item to read or remix the prompt, save favorites, or start a new generation.';

  const categoryMeta = exploreHeroCategoryRecord?.meta;
  const categoryDisplayName = exploreHeroCategoryRecord?.name?.trim() || activeExploreCategorySlug || '';
  const seoTitleTrimmed = (categoryMeta?.title && String(categoryMeta.title).trim()) || '';
  const hasDistinctSeoTitle = !!seoTitleTrimmed
    && !!categoryDisplayName
    && seoTitleTrimmed !== categoryDisplayName;

  // Only show a topline on category pages where it adds info (the category
  // display name above the SEO title). The main explore page no longer
  // renders the generic "Hand-picked prompts & outputs" eyebrow.
  const exploreDiscoveryTopline = activeExploreCategorySlug
    && exploreHeroCategoryRecord
    && hasDistinctSeoTitle
    ? categoryDisplayName
    : null;

  const exploreDiscoveryHeadline = activeExploreCategorySlug && exploreHeroCategoryRecord
    ? (seoTitleTrimmed || categoryDisplayName || defaultDiscoveryHeadline)
    : defaultDiscoveryHeadline;

  const exploreDiscoverySubtitle = activeExploreCategorySlug && exploreHeroCategoryRecord
    ? ((categoryMeta?.description && String(categoryMeta.description).trim())
      || (exploreHeroCategoryRecord.description && String(exploreHeroCategoryRecord.description).trim())
      || defaultDiscoverySubtitle)
    : defaultDiscoverySubtitle;

  const exploreCategoryPageBlocks = useMemo(() => {
    if (!activeExploreCategorySlug || !exploreHeroCategoryRecord) return [];
    const m = exploreHeroCategoryRecord.meta || {};
    const blocks = normalizeCategoryPageCopy(m.pageCopy);
    const hasFaqBlock = blocks.some((b) => b?.type === 'faq');
    if (hasFaqBlock) return blocks;
    const faqTitle = exploreHeroCategoryRecord.name
      ? `${exploreHeroCategoryRecord.name} — FAQ`
      : 'Frequently asked questions';
    const faqBlock = buildCategoryFaqBlock(m.faq, faqTitle);
    return faqBlock ? [...blocks, faqBlock] : blocks;
  }, [activeExploreCategorySlug, exploreHeroCategoryRecord]);

  const exploreCategoryPageCopyPalette = useMemo(() => {
    const slug = exploreHeroCategoryRecord?.slug || activeExploreCategorySlug;
    const fg = slug ? colorForSlug(slug) : colors.text_color_primary;
    return { fg, bg: `${fg}1f`, soft: `${fg}0d` };
  }, [exploreHeroCategoryRecord?.slug, activeExploreCategorySlug, colors.text_color_primary]);

  const queryCategoryClientSeo = useMemo(() => {
    if (!isWeb || !activeExploreCategorySlug || categorySlugProp || !exploreHeroCategoryRecord) return null;
    const m = exploreHeroCategoryRecord.meta || {};
    const title = (m.title && String(m.title).trim())
      || `${exploreHeroCategoryRecord.name || activeExploreCategorySlug} — Explore | Gab AI`;
    const description = (m.description && String(m.description).trim())
      || (exploreHeroCategoryRecord.description && String(exploreHeroCategoryRecord.description).trim())
      || `Browse curated ${(exploreHeroCategoryRecord.name || 'this').toLowerCase()} examples on Gab AI—open any piece to remix prompts or start a new generation.`;
    const keywords = (m.keywords && String(m.keywords).trim())
      || ['Gab AI explore', exploreHeroCategoryRecord.name, 'AI gallery', 'generative AI'].filter(Boolean).join(', ');
    const canonical = `/explore?category=${encodeURIComponent(activeExploreCategorySlug)}`;
    const fi = exploreHeroCategoryRecord.featuredItem;
    const image = fi?.cdnPath || fi?.thumbnailPath || null;
    return { title, description, keywords, canonical, image };
  }, [isWeb, activeExploreCategorySlug, categorySlugProp, exploreHeroCategoryRecord]);

  return (
    <ExplorePageChromeContext.Provider value={chromeContextValue}>
      <View style={[styles.container, { backgroundColor: colors.solid_color_primary }]}>
        {!activeExploreCategorySlug && (
          <MetaHead
            title="Explore — Curated AI Images, Videos, Music & 3D | Gab AI"
            description="Browse Gab AI’s curated Explore gallery: AI-generated images, videos, music, and 3D picked by our team. Open items to remix prompts, save favorites, and start new generations—filter by type, search, or category."
            canonical="/explore"
            keywords="Gab AI explore, curated AI gallery, AI generated images, AI video examples, AI music samples, AI 3D models, generative AI prompts, AI media inspiration, remix AI prompts"
            structuredData={EXPLORE_SEO_STRUCTURED_DATA}
          />
        )}
        {queryCategoryClientSeo && (
          <MetaHead
            title={queryCategoryClientSeo.title}
            description={queryCategoryClientSeo.description}
            canonical={queryCategoryClientSeo.canonical}
            keywords={queryCategoryClientSeo.keywords}
            image={queryCategoryClientSeo.image}
          />
        )}
        <ScrollView
          ref={scrollViewRef}
          style={pageStyles.pageScroll}
          contentContainerStyle={pageStyles.pageScrollContent}
          showsVerticalScrollIndicator={false}
          nestedScrollEnabled
          onScroll={handleExplorePageScroll}
          scrollEventThrottle={16}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode={Platform.OS === 'ios' || Platform.OS === 'android' ? 'on-drag' : 'none'}
          refreshControl={isNative ? (
            <RefreshControl
              refreshing={pageScrollRefreshing}
              onRefresh={handlePageScrollRefresh}
              tintColor={colors.text_color_secondary}
              colors={[colors.text_color_secondary]}
            />
          ) : undefined}
        >
          <CatalogDiscoveryHero
            topline={exploreDiscoveryTopline}
            headline={exploreDiscoveryHeadline}
            subtitle={exploreDiscoverySubtitle}
          />
          <CatalogBrowseShell
            isXS={isXS}
            bodyGap={12}
            hideRail
            suppressMobilePills
            toolbar={isXS
              ? { ...exploreBrowseToolbar, mobile: exploreMobileToolbar }
              : exploreDesktopToolbar}
          >
            <View style={pageStyles.feedWidthProbe} onLayout={onCatalogBodyLayout}>
              <ExploreFeed
                parentHandlesScroll
                items={items}
                isLoading={isLoading}
                hasMore={paginationHasMore}
                error={feedError}
                onLoadMore={handleLoadMore}
                onItemPress={handleItemPress}
                ListHeaderComponent={feedListHeader}
                onRefresh={handleRefresh}
                focusedIndex={focusedIndex}
                layoutWidth={catalogBodyWidth}
                viewMode={viewMode}
                filterFavorites={filterFavorites}
                onBrowseExplore={handleBrowseExploreFromFavorites}
              />
              {exploreCategoryPageBlocks.length > 0 && activeExploreCategorySlug ? (
                <View
                  style={{
                    width: '100%',
                    paddingTop: 28,
                    paddingBottom: 8,
                    paddingHorizontal: isXS ? 4 : 10,
                  }}
                >
                  <ToolPageCopy
                    blocks={exploreCategoryPageBlocks}
                    palette={exploreCategoryPageCopyPalette}
                    isMD={isMD || isSM}
                  />
                </View>
              ) : null}
            </View>
          </CatalogBrowseShell>

          <View style={pageStyles.footerWrap}>
            <AppDashboardFooter />
          </View>
        </ScrollView>

        <ExploreFloatingInput
          prefilledPrompt={prefill.prompt}
          prefilledMediaType={prefill.mediaType}
          prefilledModelId={prefill.modelId}
          onClear={handleClearPrefill}
        />
      </View>
    </ExplorePageChromeContext.Provider>
  );
});

ExplorePageScreen.displayName = 'ExplorePageScreen';

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default ExplorePageScreen;
